import { ApolloClient } from '@apollo/client';
import { SCENARIO_LIST_ITEM_QUERY } from './scenarios.gql';

export interface ScenarioListRecord {
	scenarioId: string;
	name: string;
	facilityId?: string;
	facility?: {
		facilityId: string;
		name: string;
	} | null;
	scenarioConditions?: {
		conditionId: string;
		condition: {
			conditionId: string;
		};
	}[];
}

interface ScenarioListItemResponse {
	scenario?: ScenarioListRecord | null;
}

export interface SelectScenarioAfterSaveOptions {
	scenarioId: string;
	client: ApolloClient<any>;
	currentScenarios: ScenarioListRecord[];
	onDataUpdate: (updatedScenarios: ScenarioListRecord[]) => void;
	onScenarioSelect: (scenario: ScenarioListRecord) => void;
	onOpenDetailsPanel?: () => void;
}

/**
 * Fetches the latest scenario data from the server and updates the list,
 * then selects and highlights the scenario in the grid and details panel.
 *
 * @param options Configuration options for selecting the scenario
 * @returns Promise that resolves when the operation is complete
 */
export async function selectScenarioAfterSave(
	options: SelectScenarioAfterSaveOptions
): Promise<void> {
	const { scenarioId, client, currentScenarios, onDataUpdate, onScenarioSelect, onOpenDetailsPanel } = options;

	try {
		// Step 1: Fetch the latest scenario data from the server
		const { data } = await client.query<ScenarioListItemResponse>({
			query: SCENARIO_LIST_ITEM_QUERY,
			variables: { id: scenarioId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedScenario = data?.scenario;

		if (!fetchedScenario) {
			console.warn(`Scenario with ID ${scenarioId} not found after save`);
			return;
		}

		// Step 2: Check if scenario exists in current list
		const existingScenarioIndex = currentScenarios.findIndex(
			s => s.scenarioId === scenarioId
		);

		let updatedScenarios: ScenarioListRecord[];

		if (existingScenarioIndex >= 0) {
			// Scenario exists - replace with updated data
			updatedScenarios = [...currentScenarios];
			updatedScenarios[existingScenarioIndex] = fetchedScenario;
		} else {
			// New scenario - append to the list
			updatedScenarios = [...currentScenarios, fetchedScenario];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedScenarios);

		// Step 4: Select the scenario (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onScenarioSelect(fetchedScenario);
			if (onOpenDetailsPanel) {
				onOpenDetailsPanel();
			}
		}, 50);
	} catch (error) {
		console.error('Error selecting scenario after save:', error);
		throw error;
	}
}
